import React, { useState, useEffect, useRef } from 'react';
import { Icons } from './Icons';

export const Editor: React.FC<{ 
  content: string; 
  onUpdate: (newContent: string) => void;
  zenMode?: boolean;
}> = ({ 
  content, 
  onUpdate,
  zenMode = false
}) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const [stats, setStats] = useState({ words: 0, chars: 0 });

  const calcStats = (text: string) => ({
    words: text.trim() ? text.trim().split(/\s+/).length : 0,
    chars: text.length
  });

  useEffect(() => {
    if (editorRef.current && content !== editorRef.current.innerHTML) {
      if (document.activeElement !== editorRef.current) {
        editorRef.current.innerHTML = content;
        const text = editorRef.current.innerText;
        setStats(calcStats(text));
      }
    }
  }, [content]);

  const handleInput = (e: React.FormEvent<HTMLDivElement>) => {
    const newContent = e.currentTarget.innerHTML;
    const text = e.currentTarget.innerText;
    setStats(calcStats(text));
    onUpdate(newContent);
  };

  const format = (command: string) => {
    document.execCommand(command, false);
    editorRef.current?.focus();
  };

  return (
    <div className="flex flex-col h-full w-full bg-white dark:bg-gray-900 relative transition-all">
        {/* Toolbar */}
        {!zenMode && (
          <div className="flex-shrink-0 flex items-center gap-1 p-2 border-b border-gray-100 dark:border-gray-800 bg-gray-50/80 dark:bg-gray-800/80 backdrop-blur select-none z-10 sticky top-0 transition-all">
              <button onMouseDown={(e) => { e.preventDefault(); format('bold'); }} className="p-1.5 hover:bg-gray-200 dark:hover:bg-gray-700 rounded text-gray-700 dark:text-gray-300 font-bold font-serif w-8 h-8 flex items-center justify-center transition-colors" title="Bold">B</button>
              <button onMouseDown={(e) => { e.preventDefault(); format('italic'); }} className="p-1.5 hover:bg-gray-200 dark:hover:bg-gray-700 rounded text-gray-700 dark:text-gray-300 italic font-serif w-8 h-8 flex items-center justify-center transition-colors" title="Italic">I</button>
              <button onMouseDown={(e) => { e.preventDefault(); format('underline'); }} className="p-1.5 hover:bg-gray-200 dark:hover:bg-gray-700 rounded text-gray-700 dark:text-gray-300 underline font-serif w-8 h-8 flex items-center justify-center transition-colors" title="Underline">U</button>
              <div className="w-px h-4 bg-gray-300 dark:bg-gray-600 mx-2"></div>
              <div className="text-xs text-gray-500 dark:text-gray-400 font-medium ml-auto flex gap-4 whitespace-nowrap px-2">
                  <span>{stats.words.toLocaleString()} words</span>
              </div>
          </div>
        )}
        
        {/* Editing Area */}
        <div 
            ref={editorRef}
            contentEditable
            onInput={handleInput}
            className={`flex-1 p-8 md:p-12 overflow-y-auto prose dark:prose-invert max-w-none focus:outline-none font-serif text-lg leading-loose text-gray-800 dark:text-gray-200 selection:bg-indigo-100 dark:selection:bg-indigo-900 pb-64 ${zenMode ? 'max-w-3xl mx-auto pt-24' : ''}`}
            suppressContentEditableWarning={true}
            style={{ whiteSpace: 'pre-wrap', minHeight: '100%' }} 
            spellCheck={false}
        />
    </div>
  );
};